import { useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { Spinner } from '../ui.js'
import { fileSize } from './text.js'
import { useDismiss } from './useDismiss.js'

export interface TrayFile {
  id: string
  name: string
  sizeBytes?: number | null
  mimeType?: string | null
}

export interface LockedFile {
  name: string
  detail: string
}

function glyph(file: { name: string; mimeType?: string | null }) {
  if (file.mimeType?.startsWith('image/') || /\.(jpe?g|png|heic|webp|gif)$/i.test(file.name)) return 'image' as const
  return 'text' as const
}

/** What goes with the message: the document the send attaches, then the files picked. */
export function AttachmentTray({
  locked,
  files,
  uploading,
  onRemove,
}: {
  locked: LockedFile[]
  files: TrayFile[]
  uploading: string[]
  onRemove: (id: string) => void
}) {
  if (locked.length === 0 && files.length === 0 && uploading.length === 0) return null
  return (
    <ul aria-label="Attachments" className="flex flex-wrap gap-2">
      {locked.map((file) => (
        <li
          key={file.name}
          title={file.detail}
          className="flex items-center gap-1.5 rounded-lg bg-ink-50 px-2.5 py-1 text-[13px] ring-1 ring-ink-200 ring-inset"
        >
          <Icon name="text" className="size-3.5 text-ink-500" />
          <span className="font-semibold text-ink-900">{file.name}</span>
          <span className="text-[12px] text-ink-500">{file.detail}</span>
        </li>
      ))}
      {files.map((file) => (
        <li
          key={file.id}
          className="flex items-center gap-1.5 rounded-lg bg-ink-50 py-1 pr-1 pl-2.5 text-[13px] ring-1 ring-ink-200 ring-inset"
        >
          <Icon name={glyph(file)} className="size-3.5 text-ink-500" />
          <span className="max-w-56 truncate font-semibold text-ink-900">{file.name}</span>
          {fileSize(file.sizeBytes) ? <span className="text-[12px] text-ink-500">{fileSize(file.sizeBytes)}</span> : null}
          <button
            type="button"
            aria-label={`Remove ${file.name}`}
            className="grid size-5 place-items-center rounded text-ink-400 hover:bg-ink-200 hover:text-ink-900"
            onClick={() => onRemove(file.id)}
          >
            <Icon name="close" className="size-3" />
          </button>
        </li>
      ))}
      {uploading.map((name) => (
        <li
          key={`uploading-${name}`}
          className="flex items-center gap-1.5 rounded-lg border border-dashed border-ink-300 bg-white px-2.5 py-1 text-[13px] text-ink-500"
        >
          <Spinner className="size-3.5" />
          <span className="max-w-56 truncate">{name}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * The paperclip: a file already on the job, or one from this computer. A new
 * file is saved to the job first, because an email's attachments are job
 * files (`readEmailAttachments`) — which is why there is no upload without one.
 */
export function AttachMenu({
  jobFiles,
  picked,
  canUpload,
  onToggle,
  onPick,
}: {
  jobFiles: TrayFile[]
  picked: string[]
  canUpload: boolean
  onToggle: (id: string) => void
  onPick: (files: File[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const wrap = useRef<HTMLDivElement>(null)

  useDismiss(wrap, open, () => setOpen(false))

  const shown = jobFiles.filter((file) => file.name.toLowerCase().includes(term.trim().toLowerCase()))

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        aria-label="Attach a file"
        aria-expanded={open}
        title="Attach a file"
        className="grid size-9 place-items-center rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink-900"
        onClick={() => setOpen((was) => !was)}
      >
        <Icon name="paperclip" className="size-4.5" />
      </button>
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        aria-label="Choose files"
        onChange={(event) => {
          const list = Array.from(event.target.files ?? [])
          if (list.length) onPick(list)
          event.target.value = ''
          setOpen(false)
        }}
      />
      {open ? (
        <div
          role="dialog"
          aria-label="Attach"
          className="absolute bottom-full left-0 z-30 mb-2 w-80 overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-ink-200"
        >
          <div className="border-b border-ink-100 px-3 pt-2.5 pb-2">
            <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">From this job</p>
            {jobFiles.length > 6 ? (
              <input
                aria-label="Find a file"
                value={term}
                placeholder="Find a file"
                className="mt-2 w-full rounded-md bg-ink-50 px-2.5 py-1.5 text-[13px] ring-1 ring-ink-200 outline-none ring-inset focus:ring-ink-400"
                onChange={(event) => setTerm(event.target.value)}
              />
            ) : null}
          </div>
          <ul className="max-h-64 overflow-y-auto p-1">
            {shown.length === 0 ? (
              <li className="px-2.5 py-2 text-[13px] text-ink-500">
                {jobFiles.length === 0 ? 'No files on this job yet.' : 'Nothing matches.'}
              </li>
            ) : (
              shown.map((file) => {
                const on = picked.includes(file.id)
                return (
                  <li key={file.id}>
                    <label className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] text-ink-800 hover:bg-ink-50">
                      <input type="checkbox" className="sr-only" checked={on} onChange={() => onToggle(file.id)} />
                      <span
                        aria-hidden="true"
                        className={cn(
                          'grid size-4.5 shrink-0 place-items-center rounded-full ring-1 ring-inset',
                          on ? 'bg-violet-600 text-white ring-violet-600' : 'ring-ink-300',
                        )}
                      >
                        {on ? <Icon name="check" className="size-3" /> : null}
                      </span>
                      <Icon name={glyph(file)} className="size-3.5 shrink-0 text-ink-400" />
                      <span className="min-w-0 flex-1 truncate">{file.name}</span>
                      {fileSize(file.sizeBytes) ? (
                        <span className="shrink-0 text-[12px] text-ink-400">{fileSize(file.sizeBytes)}</span>
                      ) : null}
                    </label>
                  </li>
                )
              })
            )}
          </ul>
          <div className="border-t border-ink-100 p-1">
            <button
              type="button"
              disabled={!canUpload}
              title={canUpload ? undefined : 'Link this email to a job to attach a new file'}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] font-medium text-ink-800 hover:bg-ink-50 disabled:cursor-not-allowed disabled:text-ink-400"
              onClick={() => input.current?.click()}
            >
              <Icon name="download" className="size-4 rotate-180 text-ink-500" />
              From your computer…
              <span className="ml-auto text-[11px] font-normal text-ink-400">or drop it here</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
